import type { RecipientBrief, RecipientHistoryEntry } from "../types";

/**
 * Mock recipient brief, matching the Robert-approved v2 mockup
 * (magic_link_recipient_page_mockup_v1.html) one to one.
 *
 * Timestamps are computed relative to load time so the rendered relative copy
 * ("Updated 3h ago", "Observed Jul 3", "Expires in 8 days") stays correct
 * whenever the page (or a screenshot) is produced. This is browser app code, not
 * a workflow script, so Date is available.
 */

const HOUR = 3_600_000;
const DAY = 86_400_000;
const now = Date.now();
const ago = (ms: number) => new Date(now - ms).toISOString();
const ahead = (ms: number) => new Date(now + ms).toISOString();

const SEEDED_HISTORY: RecipientHistoryEntry[] = [
  {
    id: "evt-match",
    actorType: "system",
    actorName: "Signal Engine",
    verb: "contacts_matched",
    detail: "Matched 2 of your contacts",
    at: ago(1 * DAY),
  },
  {
    id: "evt-open",
    actorType: "token_recipient",
    actorName: "You",
    verb: "claim",
    detail: "by opening this link",
    at: ago(3 * HOUR),
  },
];

export const RECIPIENT_TOKEN = "mlk-demo-quantum";

export const MOCK_BRIEF: RecipientBrief = {
  token: RECIPIENT_TOKEN,
  recipientName: "Robert Reynolds",
  assignerName: "Corey Whitman",
  observedAt: ago(4 * DAY),
  // Assigned a day after the signal was observed (parity amendment Item 6): the
  // top bar renders "Assigned <date>" from this, falling back to observed only
  // when a brief carries no assignment timestamp.
  assignedAt: ago(3 * DAY),
  expiresAt: ahead(8 * DAY),
  expiresInDays: 8,
  snoozeUsed: 0,
  snoozeMax: 3,
  reachedStage: "assigned",
  outcome: null,
  signal: {
    id: "sig-quantum",
    company: "Quantum Foods",
    headline:
      "Quantum Foods recalls a product line after a supplier contamination finding",
    score: 95,
    tier: 1,
    verification: "verified",
    updatedAt: ago(3 * HOUR),
    firstSeenAt: ago(4 * DAY),
    summary:
      "Quantum Foods shows a regulatory action pattern consistent with an early demand window for advisory work.",
    whyItMatters:
      "The regulatory action raises near-term exposure that maps to at least one active Ankura practice.",
    recommendedAction:
      "Confirm the internal relationship owner for Quantum Foods, then open a coordinated outreach thread before the window closes.",
    talkTrack:
      "Lead with the regulatory action and the specific operational risk it creates, not a generic capabilities pitch.",
  },
  whyForYou: {
    contextNote: "Corey assigned this from your consumer practice work.",
    capabilityMatch:
      "Your existing Quantum Foods relationship makes this a short path: 2 of your contacts sit at this account.",
  },
  accountPattern: {
    company: "Quantum Foods",
    activeCount: 3,
    priorEngagement: "Last engaged 2024, Forensics and Investigations",
    related: [
      {
        id: "sig-quantum-coldchain",
        company: "Quantum Foods",
        headline: "Quantum Foods expands cold chain capacity in the Southeast",
        score: 88,
      },
      {
        id: "sig-quantum-cfo",
        company: "Quantum Foods",
        headline: "Quantum Foods names a new chief financial officer",
        score: 82,
      },
    ],
  },
  // No asset data source exists yet (parity amendment Item 1), so the mock brief
  // carries no recommended asset — the card renders the honest empty state, the
  // same as live and the internal modal. The Attention Object Library will feed
  // real matches in a later slice.
  recommendedAsset: null,
  matchedContacts: [
    { id: "c-sam", name: "Sam Rivera", title: "Treasurer", strength: "weak" },
    {
      id: "c-taylor",
      name: "Taylor Brooks",
      title: "Head of Strategy",
      strength: "moderate",
    },
  ],
  // Persisted found contacts hydrated on brief open (no search call); the find
  // button then reads "Refresh contact search". Distinct from matchedContacts
  // (firm relationships) — these were found outside the firm on a prior search.
  foundContacts: [
    { id: "f-morgan", name: "Morgan Ellis", title: "VP Finance", strength: "moderate" },
    {
      id: "f-priya",
      name: "Priya Nair",
      title: "Director of Procurement",
      strength: "moderate",
    },
  ],
  evidence: {
    sourceLabel: "Bloomberg",
    sourceUrl: "https://www.bloomberg.com",
    retrievedAt: ago(3 * HOUR),
    status: "verified",
    deadlineAt: ahead(6 * DAY),
  },
  activation: {
    relationship: "Existing client",
    detail: "3 active engagements across 2 practices",
    monetization: "Tier A account",
  },
  ownershipLabel: "Assigned to you",
  followUpDueAt: ahead(1 * DAY),
  history: SEEDED_HISTORY,
};
